/**
 * Lifestyle Recommendation — three layout handlers, gated by DOM presence.
 *   v1 #lifestyle-room                          → hotspot pins + popups + thumb switcher
 *   v2 [data-lifestyle-triptych-tags]           → 3-banner asymmetric grid + numbered dots + inline tags
 *   v3 [data-lifestyle-triptych-caption]        → same triptych + ring dots + shared caption strip
 *
 * Runs on load AND on shopify:section:load. The theme editor swaps a section's
 * HTML in place after an edit without reloading the page, so an init that only
 * fires once leaves the new markup unwired: the slides never receive is-active,
 * is-prev or is-next and sit at their CSS defaults of opacity 0 and scale(0.92),
 * which reads as a collapsed, distorted section until the editor is reloaded.
 *
 * Each root is stamped data-lf-wired so a second pass over the same DOM is a
 * no-op and listeners cannot stack up. A re-render produces fresh elements
 * without the stamp, so those do get wired.
 */
(function () {
  'use strict';

  // Cleared on re-init so a replaced section cannot leave a timer running
  // against detached nodes.
  var infiniteTimer = null;

  /* ════════════════════════════════════════════════════════════
     v1 — HOTSPOTS
  ════════════════════════════════════════════════════════════ */
  function initHotspots() {
    var container = document.getElementById('lifestyle-room');
    if (!container || container.hasAttribute('data-lf-wired')) return;
    container.setAttribute('data-lf-wired', '');

    var roomImages = container.querySelectorAll('.lifestyle-room-img');
    var pins = container.querySelectorAll('.lifestyle-pin');
    var thumbs = document.querySelectorAll('[data-room-thumb]');
    var activeRoom = 0;

    /* Opening and closing a hotspot belongs to the shared hotspot controller
       at the foot of this file. All this layout owns is which room is on show
       and therefore which pins belong to it. */
    function closeAllCards() {
      container.querySelectorAll('.hs-pin.is-open').forEach(function (pin) {
        pin.classList.remove('is-open');
        var m = pin.querySelector('[data-hs-marker]');
        if (m) m.setAttribute('aria-expanded', 'false');
      });
    }

    function switchRoom(index) {
      if (index === activeRoom) return;
      closeAllCards();
      roomImages.forEach(function (img, i) {
        if (i === index) {
          img.classList.remove('opacity-0', 'absolute', 'inset-0');
          img.classList.add('opacity-100', 'relative');
        } else {
          img.classList.remove('opacity-100', 'relative');
          img.classList.add('opacity-0', 'absolute', 'inset-0');
        }
      });
      pins.forEach(function (pin) {
        var pinRoom = parseInt(pin.getAttribute('data-pin-room'), 10);
        pin.classList.toggle('hidden', pinRoom !== index);
      });
      thumbs.forEach(function (thumb, i) {
        if (i === index) {
          thumb.classList.remove('border-transparent', 'opacity-40', 'hover:opacity-70');
          thumb.classList.add('border-accent', 'opacity-100');
        } else {
          thumb.classList.remove('border-accent', 'opacity-100');
          thumb.classList.add('border-transparent', 'opacity-40', 'hover:opacity-70');
        }
      });
      activeRoom = index;
    }

    // Room 1's pins start visible; the rest stay hidden until their room shows.
    pins.forEach(function (pin) {
      var pinRoom = parseInt(pin.getAttribute('data-pin-room'), 10);
      pin.classList.toggle('hidden', pinRoom !== 0);
    });

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { switchRoom(i); });
    });
  }

  /* ════════════════════════════════════════════════════════════
     v2 — TRIPTYCH SLIDER
     Sides static. Middle banner auto-cycles through all rooms.
     Dots click to jump. Pauses on hover.
  ════════════════════════════════════════════════════════════ */
  function initPeekSlider() {
    var root = document.querySelector('[data-lifestyle-peek-slider]');
    if (!root || root.hasAttribute('data-lf-wired')) return;
    root.setAttribute('data-lf-wired', '');

    var track = root.querySelector('[data-peek-track]');
    var slides = root.querySelectorAll('[data-peek-slide]');
    var dots = root.querySelectorAll('[data-peek-dot]');
    if (!track || slides.length < 2) return;

    var speed = parseInt(root.getAttribute('data-slider-speed'), 10) || 4500;
    var current = 0;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var GAP = 16;

    function position() {
      var viewportEl = root.querySelector('.lf-peek-viewport');
      if (!viewportEl || !slides[0]) return;
      var vw = viewportEl.offsetWidth;
      var sw = slides[0].offsetWidth;
      var center = (vw - sw) / 2;
      var x = center - current * (sw + GAP);
      track.style.transform = 'translateX(' + x + 'px)';
      slides.forEach(function (s, i) { s.classList.toggle('is-active', i === current); });
      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === current); });
    }

    function go(idx) {
      current = ((idx % slides.length) + slides.length) % slides.length;
      position();
    }

    function start() {
      if (reduced) return;
      stop();
      infiniteTimer = setInterval(function () { go(current + 1); }, speed);
    }
    function stop() { if (infiniteTimer) { clearInterval(infiniteTimer); infiniteTimer = null; } }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { go(i); start(); });
    });
    slides.forEach(function (slide, i) {
      slide.addEventListener('click', function (e) {
        if (i !== current) { e.preventDefault(); go(i); start(); }
      });
    });

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    window.addEventListener('resize', position);
    window.addEventListener('load', position);
    position();
    start();
  }

  /* ════════════════════════════════════════════════════════════
     v3 — TRIPTYCH CAPTION
     All banners visible. Hovering ANY ring dot in any banner
     morphs the shared caption strip below the row.
  ════════════════════════════════════════════════════════════ */
  function initInfiniteSlider() {
    var root = document.querySelector('[data-lifestyle-infinite-slider]');
    if (!root || root.hasAttribute('data-lf-wired')) return;
    root.setAttribute('data-lf-wired', '');

    var slides = root.querySelectorAll('[data-inf-slide]');
    var dots = root.querySelectorAll('[data-inf-dot]');
    var n = slides.length;
    if (n < 1) return;

    var speed = parseInt(root.getAttribute('data-slider-speed'), 10) || 4500;
    var current = 0;
    var timer = null;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Below 768px the section stylesheet turns the viewport into a scroll-snap
       rail and the browser owns the motion. Everything in here that moves the
       slider itself has to stand down at that width, or the finger and the
       script end up pulling the same photo in two directions. Read live rather
       than cached so a rotation lands on the right behaviour without a reload. */
    var railQuery = window.matchMedia('(max-width: 767px)');
    function rail() { return railQuery.matches; }

    function update() {
      var prevIdx = (current - 1 + n) % n;
      var nextIdx = (current + 1) % n;
      slides.forEach(function (s, i) {
        s.classList.remove('is-active', 'is-prev', 'is-next');
        if (i === current) s.classList.add('is-active');
        else if (n > 1 && i === prevIdx) s.classList.add('is-prev');
        else if (n > 2 && i === nextIdx) s.classList.add('is-next');
      });
      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === current); });
    }

    function go(idx) {
      current = ((idx % n) + n) % n;
      if (rail()) { scrollToSlide(current); return; }
      update();
    }

    /* Once the visitor drives the slider themselves it stays on their scene.
       Auto-advance exists to show the scenes to someone who has not engaged;
       the moment they swipe, tap an arrow or pick a dot, continuing to yank the
       photo away is just fighting them. Hover pause below still applies to the
       passive case on a mouse. */
    var takenOver = false;

    function start() {
      if (reduced || n < 2 || takenOver || rail()) return;
      stop();
      timer = setInterval(function () { go(current + 1); }, speed);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function takeOver() { takenOver = true; stop(); }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { takeOver(); go(i); });
    });

    var prevBtn = root.querySelector('[data-inf-prev]');
    var nextBtn = root.querySelector('[data-inf-next]');
    if (prevBtn) prevBtn.addEventListener('click', function () { takeOver(); go(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { takeOver(); go(current + 1); });

    slides.forEach(function (slide) {
      slide.addEventListener('click', function (e) {
        if (rail()) return;
        if (slide.classList.contains('is-prev')) { e.preventDefault(); takeOver(); go(current - 1); }
        else if (slide.classList.contains('is-next')) { e.preventDefault(); takeOver(); go(current + 1); }
      });
    });

    /* Drag to change scene. Pointer events cover touch, pen AND mouse: people
       drag the photo on a desktop too, and having it do nothing there reads as
       broken. The viewport sets touch-action: pan-y so a vertical drag still
       scrolls the page. */
    var viewport = root.querySelector('.lf-inf-viewport') || root;

    /* ── The rail half ───────────────────────────────────────────────────
       Below the breakpoint this is a native scroll container, so the browser
       owns the motion and the finger gets momentum, snap and rubber-band for
       free, the same as every other rail on the site.

       Two things sit on top of it.

       IT LOOPS. A scroll container has a first child and a last child, so on
       its own it stops at both ends. The scenes are cloned so the rail holds
       three identical copies and parks in the middle one; when a scroll comes
       to rest outside that copy, the scroll position is moved by exactly one
       copy's width. What is either side is the same photo, so the move cannot
       be seen, and the shopper can keep going in either direction forever.

       The move waits for the scroll to STOP. Rewriting scrollLeft while iOS is
       still running its momentum kills the momentum, which would feel worse
       than the stop it is there to prevent. Three copies is far more than one
       flick can cross, so there is always room to wait.

       IT KEEPS THE LOOK. The scene nearest the middle gets .is-centred, which
       is what the stylesheet steps up to full size and full strength. That is
       the same peek carousel as before; only what moves it has changed. */
    var railSlides = null;
    var railCopies = 1;

    function buildLoop() {
      if (railSlides || n < 2) return;
      var track = root.querySelector('[data-inf-track]');
      if (!track) return;
      var originals = [];
      for (var i = 0; i < n; i++) originals.push(slides[i]);

      for (var copy = 0; copy < 2; copy++) {
        originals.forEach(function (original) {
          var clone = original.cloneNode(true);
          clone.setAttribute('data-lf-clone', '');
          /* A clone must be invisible to the theme editor, or selecting a
             hotspot in the sidebar would jump to a copy nobody added. */
          clone.removeAttribute('data-shopify-editor-block');
          clone.querySelectorAll('[data-shopify-editor-block]').forEach(function (el) {
            el.removeAttribute('data-shopify-editor-block');
          });
          /* and it must look unwired, or its pins never get a click handler */
          clone.querySelectorAll('[data-hs-wired]').forEach(function (el) {
            el.removeAttribute('data-hs-wired');
          });
          clone.querySelectorAll('.hs-pin.is-open').forEach(function (el) {
            el.classList.remove('is-open');
          });
          track.appendChild(clone);
        });
      }

      railCopies = 3;
      railSlides = [].slice.call(track.querySelectorAll('[data-inf-slide]'));
      if (window.AykahHotspots && window.AykahHotspots.rewire) window.AykahHotspots.rewire();
    }

    /* Width of one copy: where copy two starts, less where copy one starts. */
    function copyWidth() {
      if (!railSlides || railCopies < 2) return 0;
      return railSlides[n].offsetLeft - railSlides[0].offsetLeft;
    }

    /* Scroll offset that parks slide `idx` (an index into railSlides) dead
       centre. Falls back to the plain rail when there are no clones. */
    function centreOffset(idx) {
      var el = (railSlides || slides)[idx];
      if (!el) return 0;
      return el.offsetLeft - (viewport.clientWidth - el.offsetWidth) / 2;
    }

    /* Keep the rail inside the middle copy. Silent, because the copies are
       identical. */
    function normaliseLoop() {
      var w = copyWidth();
      if (!w) return;
      var home = centreOffset(n);                    // first scene, middle copy
      var drift = viewport.scrollLeft - home;
      if (Math.abs(drift) <= w / 2) return;

      /* The jump lands on the same photo in a different copy, and that is a
         different ELEMENT. Left alone, the one being left behind animates down
         from full size while its twin animates up into it, so the scene at the
         seam appears to pulse: out and back in, over the .35s the look takes.
         Carry the marker across with the transition switched off, so the swap
         happens between frames and cannot be seen. */
      root.classList.add('is-jumping');
      viewport.scrollLeft += drift > 0 ? -w : w;
      markCentred();
      void viewport.offsetWidth;                     // flush it unanimated
      requestAnimationFrame(function () { root.classList.remove('is-jumping'); });
    }

    function markCentred() {
      var list = railSlides || [].slice.call(slides);
      var mid = viewport.scrollLeft + viewport.clientWidth / 2;
      var best = 0, bestDist = Infinity;
      for (var i = 0; i < list.length; i++) {
        var c = list[i].offsetLeft + list[i].offsetWidth / 2;
        var d = Math.abs(c - mid);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      for (var j = 0; j < list.length; j++) list[j].classList.toggle('is-centred', j === best);

      var logical = best % n;
      if (logical === current) return;
      current = logical;
      dots.forEach(function (dot, k) { dot.classList.toggle('is-active', k === current); });

      /* Scrolled to a different scene, so the pin the strip was drawing is no
         longer the one being looked at. Closing it clears the strip too. */
      if (window.AykahHotspots && window.AykahHotspots.closeAll) window.AykahHotspots.closeAll();
    }

    /* A dot jumps to the nearest copy of that scene, so it never scrolls the
       long way round. */
    function scrollToSlide(i) {
      if (!rail()) return;
      var list = railSlides || [].slice.call(slides);
      var here = viewport.scrollLeft;
      var target = null, bestDist = Infinity;
      for (var k = 0; k < list.length; k++) {
        if (k % n !== i) continue;
        var off = centreOffset(k);
        var d = Math.abs(off - here);
        if (d < bestDist) { bestDist = d; target = off; }
      }
      if (target === null) return;
      viewport.scrollTo({ left: target, behavior: reduced ? 'auto' : 'smooth' });
    }

    var railTicking = false, settleTimer = null;
    viewport.addEventListener('scroll', function () {
      if (!rail()) return;
      if (!railTicking) {
        railTicking = true;
        requestAnimationFrame(function () { markCentred(); railTicking = false; });
      }
      clearTimeout(settleTimer);
      settleTimer = setTimeout(normaliseLoop, 140);
    }, { passive: true });

    /* ── The caption strip ───────────────────────────────────────────────
       Phones only. It draws whichever pin is open, and holds no state of its
       own: it listens for the events the hotspot code fires and reads the
       product straight out of that pin's card, so the card stays the single
       source of the name, price, thumbnail and link. */
    var capWrap  = root.querySelector('[data-lf-caption-wrap]');
    var capLink  = root.querySelector('[data-lf-caption]');
    var capIdle  = root.querySelector('[data-lf-caption-idle]');
    var capImg   = root.querySelector('[data-lf-caption-img]');
    var capName  = root.querySelector('[data-lf-caption-name]');
    var capPrice = root.querySelector('[data-lf-caption-price]');

    function clearCaption() {
      if (!capLink) return;
      capLink.hidden = true;
      if (capIdle) capIdle.hidden = false;
    }

    function showCaption(pin) {
      if (!capLink) return;
      var card = pin.querySelector('.hs-card');
      if (!card) return;

      var name  = card.querySelector('.hs-name');
      var price = card.querySelector('.hs-price');
      var thumb = card.querySelector('.hs-thumb img');

      capName.textContent  = name ? name.textContent.trim() : '';
      capPrice.textContent = price ? price.textContent.trim() : '';

      if (thumb && thumb.getAttribute('src')) {
        capImg.src = thumb.getAttribute('src');
        capImg.hidden = false;
      } else {
        capImg.removeAttribute('src');
        capImg.hidden = true;
      }

      /* A hotspot whose product cannot render on the storefront has a span
         here rather than a link. Show what it says, but do not offer a tap
         that goes nowhere. */
      var href = card.tagName === 'A' ? card.getAttribute('href') : null;
      if (href) {
        capLink.setAttribute('href', href);
        capLink.removeAttribute('aria-disabled');
        capLink.style.pointerEvents = '';
      } else {
        capLink.removeAttribute('href');
        capLink.setAttribute('aria-disabled', 'true');
        capLink.style.pointerEvents = 'none';
      }

      if (capIdle) capIdle.hidden = true;
      capLink.hidden = false;
    }

    document.addEventListener('aykah:hotspot-open', function (e) {
      if (!rail() || !e.detail || !root.contains(e.detail.pin)) return;
      showCaption(e.detail.pin);
    });
    document.addEventListener('aykah:hotspot-close', function () {
      if (!rail()) return;
      clearCaption();
    });

    /* Leaving the rail has to undo everything enterRail built. Without this
       the two clone copies stayed in the track and the viewport kept the
       rail's scroll offset, so the desktop triptych rendered shifted left with
       dead space beside it. The theme editor hits this constantly: the preview
       iframe crosses the breakpoint every time the settings panel opens or
       closes, and nothing was resetting on the way back up. */
    function exitRail() {
      if (!railSlides) return;
      var track = root.querySelector('[data-inf-track]');
      if (track) {
        track.querySelectorAll('[data-lf-clone]').forEach(function (c) {
          if (c.parentNode) c.parentNode.removeChild(c);
        });
      }
      railSlides = null;
      root.classList.remove('is-jumping');
      slides.forEach(function (sl) { sl.classList.remove('is-centred'); });
      viewport.scrollLeft = 0;
    }

    function enterRail() {
      buildLoop();
      stop();
      /* Start in the middle copy so there is a scene to reach in both
         directions from the very first swipe. */
      if (railSlides) viewport.scrollLeft = centreOffset(n);
      markCentred();
    }
    var startX = 0, startY = 0, tracking = false, decided = false, horizontal = false;
    var dragged = false;
    var THRESHOLD = 45;

    function onDown(e) {
      if (rail()) return;
      tracking = true; decided = false; horizontal = false; dragged = false;
      startX = e.clientX; startY = e.clientY;
      /* Touch has no hover, so a finger landing is the only signal that someone
         is looking at this photo. Hold it immediately. A mouse already has
         hover pause, so there we wait for a real drag and leave a stray click
         alone. */
      if (e.pointerType !== 'mouse') takeOver();
    }
    function onMove(e) {
      if (!tracking) return;
      var dx = e.clientX - startX, dy = e.clientY - startY;
      if (!decided) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        decided = true;
        horizontal = Math.abs(dx) > Math.abs(dy);
      }
      if (horizontal && Math.abs(dx) > 10) dragged = true;
    }
    function onUp(e) {
      if (!tracking) return;
      tracking = false;
      if (!horizontal) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) < THRESHOLD) return;
      takeOver();
      go(dx < 0 ? current + 1 : current - 1);
    }
    if (window.PointerEvent) {
      viewport.addEventListener('pointerdown', onDown, { passive: true });
      viewport.addEventListener('pointermove', onMove, { passive: true });
      viewport.addEventListener('pointerup', onUp, { passive: true });
      viewport.addEventListener('pointercancel', function () { tracking = false; dragged = false; }, { passive: true });
      /* A drag must not also fire the click underneath it, or releasing over a
         side slide would navigate twice and releasing over a hotspot would open
         a product the visitor never tapped. Capture phase, so it lands before
         the slide and pin handlers. */
      viewport.addEventListener('click', function (e) {
        if (!dragged) return;
        e.preventDefault(); e.stopPropagation();
        dragged = false;
      }, true);
      /* Stop the browser's native image-drag ghost from hijacking the gesture. */
      viewport.addEventListener('dragstart', function (e) { e.preventDefault(); });
    }

    /* Trackpad: a two-finger horizontal swipe arrives as wheel events carrying
       deltaX, with no button ever pressed. That is what most people mean by
       "swipe" on a laptop, so it moves the scene directly rather than making
       them click and drag.

       Vertical wheel is left alone so the page still scrolls. Horizontal is
       preventDefault'ed, which also stops the browser treating the same gesture
       as swipe-to-go-back and navigating away from the page.

       One flick must move exactly one scene. That cannot be done on a timer:
       momentum keeps firing for the better part of a second, so any fixed lock
       expires mid-stream and the tail alone clears the threshold again, which
       is a second scene the visitor never asked for.

       So the gesture is ended by silence, not by the clock. Every wheel event
       pushes the end back; only a real gap means the fingers are done. Between
       the first advance and that gap the slider ignores everything, however
       long the tail runs. */
    var wheelAccum = 0, gestureSpent = false, endTimer = null;
    var WHEEL_THRESHOLD = 60;
    var GESTURE_GAP = 260;

    viewport.addEventListener('wheel', function (e) {
      if (rail()) return;                                      // the rail scrolls itself
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;   // vertical: page scroll
      e.preventDefault();
      takeOver();

      clearTimeout(endTimer);
      endTimer = setTimeout(function () { gestureSpent = false; wheelAccum = 0; }, GESTURE_GAP);

      if (gestureSpent) return;
      wheelAccum += e.deltaX;
      if (Math.abs(wheelAccum) < WHEEL_THRESHOLD) return;

      go(wheelAccum > 0 ? current + 1 : current - 1);
      wheelAccum = 0;
      gestureSpent = true;
    }, { passive: false });

    // Pause sliding on hover/focus anywhere in the section (mouse + keyboard)
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    if (rail()) {
      enterRail();
    } else {
      update();
      start();
    }
    /* Crossing the breakpoint swaps which half is in charge. The clones stay
       once built: above the breakpoint they are positioned like every other
       slide and the desktop slider only ever looks at the first n. */
    if (railQuery.addEventListener) {
      railQuery.addEventListener('change', function () {
        if (rail()) { enterRail(); return; }
        exitRail();
        update();
        start();
      });
    }
    window.addEventListener('resize', function () {
      if (rail() && railSlides) markCentred();
    });
  }

  function init() {
    initHotspots();
    initPeekSlider();
    initInfiniteSlider();
  }

  init();
  // the theme editor re-renders a section without reloading the page
  document.addEventListener('shopify:section:load', function () {
    if (infiniteTimer) { clearInterval(infiniteTimer); infiniteTimer = null; }
    init();
  });
})();

/**
 * v4 "Panels" — one panel per room, hotspots owned by the room block.
 * Separate file-level IIFE so it is independent of the three older layouts.
 * Same contract: idempotent, and re-wires after a theme-editor save.
 */
(function () {
  'use strict';

  function initPanels() {
    var root = document.querySelector('[data-lfp]');
    if (!root || root.hasAttribute('data-lfp-wired')) return;
    root.setAttribute('data-lfp-wired', '');

    var panels = root.querySelectorAll('[data-lfp-panel]');
    var thumbs = root.querySelectorAll('[data-lfp-thumb]');
    if (!panels.length) return;

    function closeCards() {
      root.querySelectorAll('.hs-pin.is-open').forEach(function (p) {
        p.classList.remove('is-open');
        var m = p.querySelector('[data-hs-marker]');
        if (m) m.setAttribute('aria-expanded', 'false');
      });
    }

    function show(index) {
      closeCards();
      panels.forEach(function (p, i) { p.classList.toggle('is-active', i === index); });
      thumbs.forEach(function (t, i) {
        t.classList.toggle('is-active', i === index);
        t.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { show(i); });
    });

    // Hotspots themselves are handled by the shared controller below.
  }

  initPanels();
  document.addEventListener('shopify:section:load', initPanels);
})();


/**
 * Shoppable hotspot — Figma 259:2.
 * One controller for every layout that draws pins, so behaviour cannot drift
 * between them. Idempotent and re-wires after a theme-editor save.
 */
(function () {
  'use strict';

  function closeAll(except) {
    document.querySelectorAll('.hs-pin.is-open').forEach(function (pin) {
      if (pin === except) return;
      pin.classList.remove('is-open');
      var m = pin.querySelector('[data-hs-marker]');
      if (m) m.setAttribute('aria-expanded', 'false');
      var card = pin.querySelector('.hs-card');
      if (card && card.tagName === 'A') card.setAttribute('tabindex', '-1');
    });

    /* Nothing is open now, so anything drawing the open pin elsewhere, like
       the phone caption strip, can put itself away. Not fired when a pin is
       being swapped for another, because open() announces that itself. */
    if (!except) document.dispatchEvent(new CustomEvent('aykah:hotspot-close'));
  }

  /* The card opens to the right of the pin. Close to the right edge of its
     stage that would push it out of view, so it flips to the other side.

     The flip on its own is not enough, and that is what shipped: it only ever
     asked whether the card was off the RIGHT, so a card sent left had nothing
     checking THAT side, and the slide clips what leaves it. On a phone the
     stage is about 253px and the card up to 260px, so there are pin positions
     where neither side fits at all and no amount of flipping helps.

     So after the flip the card is nudged back inside on both axes, and its
     width is capped to the stage. A card that overlaps its own pin is worth
     it: a shopper can read an overlapping card, and cannot read a clipped one.

     MARGIN is the breathing room kept between card and stage edge. */

  var CARD_MARGIN = 10;

  function placeCard(pin) {
    pin.classList.remove('is-flipped');
    var card = pin.querySelector('.hs-card');
    var stage = pin.closest('[data-lf-stage]') || pin.offsetParent;
    if (!card || !stage) return;

    /* Clear last run's correction, or each call would compound the previous. */
    card.style.setProperty('--hs-dx', '0px');
    card.style.setProperty('--hs-dy', '0px');
    card.style.maxWidth = '';

    /* On a phone the card is out of the flow entirely and the caption strip
       below the rail carries the product instead, so there is nothing to place
       and its rects would all read zero. */
    if (getComputedStyle(card).display === 'none') return;

    var s = stage.getBoundingClientRect();

    /* A card wider than its stage can never be placed, only chosen between two
       bad clips, so cap it first and let the text wrap inside. */
    var room = s.width - CARD_MARGIN * 2;
    if (room > 0 && card.getBoundingClientRect().width > room) {
      card.style.maxWidth = room + 'px';
    }

    if (card.getBoundingClientRect().right > s.right - 8) pin.classList.add('is-flipped');

    /* Re-measure: the flip moved it, and the cap may have reflowed it. */
    var c = card.getBoundingClientRect();
    var dx = 0, dy = 0;
    if (c.left < s.left + CARD_MARGIN) dx = (s.left + CARD_MARGIN) - c.left;
    else if (c.right > s.right - CARD_MARGIN) dx = (s.right - CARD_MARGIN) - c.right;
    if (c.top < s.top + CARD_MARGIN) dy = (s.top + CARD_MARGIN) - c.top;
    else if (c.bottom > s.bottom - CARD_MARGIN) dy = (s.bottom - CARD_MARGIN) - c.bottom;

    if (dx) card.style.setProperty('--hs-dx', Math.round(dx) + 'px');
    if (dy) card.style.setProperty('--hs-dy', Math.round(dy) + 'px');
  }

  function open(pin) {
    closeAll(pin);
    pin.classList.add('is-open');
    /* Which pin is open is decided here and only here. Anything that draws it
       elsewhere, like the phone caption strip, listens rather than keeping its
       own copy, so the two cannot drift apart. */
    document.dispatchEvent(new CustomEvent('aykah:hotspot-open', { detail: { pin: pin } }));
    var m = pin.querySelector('[data-hs-marker]');
    if (m) m.setAttribute('aria-expanded', 'true');
    var card = pin.querySelector('.hs-card');
    if (card && card.tagName === 'A') card.setAttribute('tabindex', '0');
    placeCard(pin);
  }

  function initShoppableHotspots() {
    document.querySelectorAll('[data-hs-marker]').forEach(function (marker) {
      if (marker.hasAttribute('data-hs-wired')) return;
      marker.setAttribute('data-hs-wired', '');
      marker.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var pin = marker.closest('.hs-pin');
        if (!pin) return;
        if (pin.classList.contains('is-open')) closeAll();
        else open(pin);
      });
    });

    if (!document.documentElement.hasAttribute('data-hs-global')) {
      document.documentElement.setAttribute('data-hs-global', '');
      // A tap outside, or Escape, closes. Clicks inside the card are its link.
      document.addEventListener('click', function (e) {
        if (!e.target.closest('.hs-pin')) closeAll();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeAll();
      });
      window.addEventListener('resize', function () {
        document.querySelectorAll('.hs-pin.is-open').forEach(placeCard);
      });
    }
  }

  initShoppableHotspots();
  document.addEventListener('shopify:section:load', initShoppableHotspots);

  /* The infinite rail clones its scenes, and a clone carries the wired flag of
     the pin it came from, so it would be skipped. Cloners call this after
     clearing that flag on their copies. */
  window.AykahHotspots = {
    rewire: initShoppableHotspots,
    closeAll: function () { closeAll(); }
  };
})();
